/* eslint-disable prettier/prettier */
/* eslint-disable no-shadow */
/* eslint-disable react/prop-types */
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import { Box, TableCell, TableRow, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { css, StyleSheet } from "aphrodite";
import { Helmet } from "react-helmet";
import Button from "../../../components/common/Button";
import Loader from "../../../components/common/Loader/Loader";
import Table from "../../../components/common/new/Table/Table";
import Pagination from "../../../components/common/new/Pagination/Pagination";
import { REQUEST_TRANSFER_COL } from "../../../contants/table/friendSearch";
import { SET_SELECTED_INTRODUCTION_FROM_SEARCH } from "../../../redux/actionTypes/dashboard";
import ViewIntroduction from "../../../services/api/viewIntroduction";
import tableStyle from "../../../theme/styles/table";
import style from "./style";
import { performOperation } from "../../member/MemberSearch/utils";
import { Constants } from "../../../contants";

const DEFAULT_TYPE = "assignedToMe";

const getRows = (data, onClickHandler) => {
  const hasFriendId = Boolean(data?.[0]?.friendId);

  const toolTipTitle = () => {
    if (hasFriendId) return "Change to Member";
    return "Make New Friend";
  };

  return (data || []).map((row, idx) => (
    <TableRow
      key={
        row.introductionId ||
        row.friendId ||
        row.mobileNumber ||
        row.emailId ||
        idx
      }
    >
      <TableCell sx={{ width: "18%" }}>
        {row.fullName || "-"} <br />
        <LocationOnIcon style={tableStyle.locationIcon} />{" "}
        {row.addressLine1 || "-"}
      </TableCell>

      <TableCell>
        <PhoneIcon style={tableStyle.icon} /> {row.mobileNumber || "-"} <br />
        <EmailIcon style={tableStyle.icon} /> {row.emailId || "-"}
      </TableCell>

      <TableCell>
        {[
          row?.areaName,
          row?.clusterName,
          row?.zoneName,
          row?.regionName,
          row?.chapterName,
          row?.districtName,
          row?.blockName,
        ]
          .filter(Boolean)
          .join("/ ")}
      </TableCell>

      {onClickHandler ? (
        <TableCell align="right">
          <Button
            variant="outlined"
            label={toolTipTitle()}
            style={{ cursor: "pointer", width: "180px", lineHeight: "1" }}
            onClick={() => onClickHandler(row)}
          />
        </TableCell>
      ) : null}
    </TableRow>
  ));
};

const normalizePagedResponse = (res) => {
  // Supports:
  // 1) { content: [], totalPages, totalElements }
  // 2) [] (plain array)
  let content = [];
  let totalPages = 0;
  let totalElements = 0;

  if (Array.isArray(res?.content)) {
    content = res.content;
    totalPages = typeof res.totalPages === "number" ? res.totalPages : 0;
    totalElements =
      typeof res.totalElements === "number"
        ? res.totalElements
        : content.length;
    return { content, totalPages, totalElements };
  }

  if (Array.isArray(res)) {
    content = res;
    totalPages = content.length ? 1 : 0;
    totalElements = content.length;
    return { content, totalPages, totalElements };
  }

  return { content, totalPages, totalElements };
};

function SearchNewFriend() {
  const TITLE = "Search New Friend - BSG";

  const [introductions, setIntroductions] = useState([]);
  const [noResult, setNoResult] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(Constants.DEFAULT_PAGE_SIZE);


  const Style = StyleSheet.create({
    conatiner: {
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      "@media (max-width: 768px)": {
        paddingLeft: "10px",
        minHeight: "1400px",
      },
    },
  });

  const loadAssignedIntroductions = useCallback(
    async (pageNumber = 0, size = Constants.DEFAULT_PAGE_SIZE) => {
      try {
        setLoading(true);
        setNoResult(false);

        const res = await ViewIntroduction.getIntroductionData({
          pageNumber,
          pageSize: size,
          type: DEFAULT_TYPE,
        });

        const normalized = normalizePagedResponse(res);

        setIntroductions(normalized.content);
        setCurrentPage(pageNumber);
        setTotalPages(normalized.totalPages);
        setTotalElements(normalized.totalElements);
        setPageSize(size);

        if (!normalized.content.length) setNoResult(true);

        setLoading(false);
      } catch (error) {
        setIntroductions([]);
        setNoResult(true);
        setLoading(false);
        Sentry.captureException(error);
      }
    },
    []
  );

  useEffect(() => {
    loadAssignedIntroductions(0, Constants.DEFAULT_PAGE_SIZE);

    return () => {
      if (ViewIntroduction?.getIntroductionData?.cancel) {
        ViewIntroduction.getIntroductionData.cancel();
      }
    };
  }, [loadAssignedIntroductions]);

  const onClickNewFriend = () => {
    navigate("/new-friend/create");
  };

  const onSelectIntro = (data) => {
    if (!data) return;

    dispatch({
      type: SET_SELECTED_INTRODUCTION_FROM_SEARCH,
      payload: data,
    });

    navigate(`/new-friend/change/${data.introductionId}`);
  };

  const onPaginationClick = (page) => {
    loadAssignedIntroductions(Number(page), pageSize);
  };

  const onPageSizeClick = (operator) => {
    const newPageSize = performOperation(
      pageSize,
      Constants.DEFAULT_PAGE_SIZE,
      operator
    );
    loadAssignedIntroductions(0, newPageSize);
  };

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>

      <Box className={css(Style.conatiner)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 20px",
            border: "1px solid #e0e0e0",
            borderRadius: "10px",
            backgroundColor: "#fafafa",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            mb: 2,
            gap: "8px",
            flexWrap: "wrap",
            "@media (max-width: 768px)": {
              flexDirection: "column",
              alignItems: "flex-start",
            },
          }}
        >
          <Typography sx={style.TableTitle}>List of Introductions</Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              "@media (max-width: 768px)": {
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#757575",
                fontSize: "13px",
              }}
            >
              If the person you wish to add is not listed below, then click here
            </Typography>
            <Button
              variant="outlined"
              label="Enter New Friend"
              onClick={onClickNewFriend}
              style={{ minWidth: "170px" }}
            />
          </Box>
        </Box>

        <Box>
          {introductions.length ? (
            <>
              <Table
                columns={REQUEST_TRANSFER_COL}
                rows={getRows(introductions, onSelectIntro)}
                hasAction
                scrollable
                tableBodyTheme="#F6F6F6"
                tableHeadCellStyle={{
                  ...style.tableHeadCells,
                  textAlign: "left",
                }}
              />

              <Box style={style.pagination}>
                <Pagination
                  totalPages={totalPages}
                  totalResults={totalElements}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  onChange={onPaginationClick}
                  onPageSizeClick={onPageSizeClick}
                />
              </Box>
            </>
          ) : null}

          {noResult ? (
            <Box
              sx={{
                p: "110px",
                display: "flex",
                justifyContent: "space-evenly",
                alignItems: "center",
                marginTop: 10,
              }}
            >
              <Typography
                variant="p"
                sx={{
                  paddingBottom: 10,
                  color: "#616161",
                  fontSize: "20px",
                  fontWeight: "400",
                }}
              >
                No introductions found.
              </Typography>
            </Box>
          ) : null}
        </Box>

        {loading ? <Loader /> : null}
      </Box>
    </>
  );
}

export default SearchNewFriend;
